import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import type { FC } from "react"
import { useMemo, useRef } from "react"
import { useFieldArray, useFormContext, useFormState, useWatch } from "react-hook-form"

import {
  BLANK_TARIFF_ITEM,
  COST_BREAKDOWN_CURRENCY_FIELD_PROPS,
  COST_BREAKDOWN_PERCENTAGE_FIELD_PROPS,
} from "../../constants"
import { CostBreakdownStandardField } from "../../types"

import { AmountPercentageFieldPair } from "./AmountPercentageFieldPair"
import { AtypicalSurchargesFormGroup } from "./AtypicalSurchargesFormGroup"

import { Button } from "@/components/common/Button"
import { FormCheckbox, FormNumericField } from "@/components/UiKit/FormControls"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { useAmountPercentageCalculations, useValidationTriggers } from "@/screens/Procurement/CostBreakdown"
import { useProductCalculations } from "@/screens/Procurement/CostBreakdown/hooks/useProductCalculations"
import { getCostBreakdownProductFieldPaths, hasValidationErrors } from "@/screens/Procurement/CostBreakdown/utils"
import type { PurchaseOrderLineWithReceiptContext } from "@/screens/WarehouseReceiving/types"
import { isEmptyValue } from "@/utils/forms"

interface CostBreakdownProductsExpandedContentProps {
  row: Row<PurchaseOrderLineWithReceiptContext>
  goodsReceipt: GoodsReceipt
}

export const CostBreakdownProductsExpandedContent: FC<CostBreakdownProductsExpandedContentProps> = ({
  row,
  goodsReceipt,
}) => {
  const purchaseOrderLine = row.original

  // Since both the table and form products use the same order line structure (getGoodsReceiptPurchaseOrderLines),
  // the row index directly corresponds to the form product index - no complex mapping needed!
  const productIndex = row.index

  const { control } = useFormContext()
  const { errors } = useFormState({ control })

  const fieldPaths = getCostBreakdownProductFieldPaths(productIndex)

  const { fields: tariffFields, append: appendTariff } = useFieldArray({
    control,
    name: fieldPaths.tariffs,
  })

  // Initialize refs needed for calculations
  const isRecalculatingFromBareCost = useRef<boolean>(false)

  const { totalBareCost } = useProductCalculations({
    productIndex,
    purchaseOrderLine,
    goodsReceipt,
  })

  useValidationTriggers({ productIndex })
  useAmountPercentageCalculations({
    productIndex,
    totalBareCost,
    isRecalculatingFromBareCost,
  })

  const pricePerUom = useWatch({
    control,
    name: fieldPaths.pricePerUom,
  })
  const bareCostPerUom = useWatch({
    control,
    name: fieldPaths.bareCostPerUom,
  })
  const bareCostPerMetricUom = useWatch({
    control,
    name: fieldPaths.bareCostPerMetricUom,
  })
  const totalBareCostField = useWatch({
    control,
    name: fieldPaths.totalBareCost,
  })
  const isBareCostEmpty =
    isEmptyValue(bareCostPerUom) ||
    isEmptyValue(bareCostPerMetricUom) ||
    isEmptyValue(totalBareCostField) ||
    totalBareCostField === 0
  const tariffValues = useWatch({
    control,
    name: fieldPaths.tariffs,
  })

  // Check if any tariff field has validation errors
  const hasTariffErrors = useMemo(
    () => {
      const tariffErrors = (errors as any)?.products?.[productIndex]?.tariffs
      return hasValidationErrors(tariffErrors)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors, productIndex, tariffValues]
  )

  return (
    <Box className='space-y-6 px-4 py-10'>
      <Box className='grid grid-cols-12 gap-6'>
        <Box className='col-span-5 flex items-start gap-2'>
          <FormNumericField
            control={control}
            name={fieldPaths.pricePerUom}
            label='Price per US UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          />

          <span className='mt-7 text-gray-500'>=</span>

          <FormNumericField
            control={control}
            name={fieldPaths.pricePerMetricUom}
            label='Price per Metric UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          />
        </Box>

        <Box className='col-span-7 flex items-start gap-2'>
          <FormNumericField
            control={control}
            name={fieldPaths.bareCostPerUom}
            label='Bare Cost per US UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            sx={{ flex: 1 }}
          />

          <span className='mt-7 text-gray-500'>=</span>

          <FormNumericField
            control={control}
            name={fieldPaths.bareCostPerMetricUom}
            label='Bare Cost per Metric UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            sx={{ flex: 1 }}
          />

          <span className='mt-7 text-gray-500'>=</span>

          <FormNumericField
            control={control}
            name={fieldPaths.totalBareCost}
            label='Total Bare Cost'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            sx={{ flex: 1 }}
          />
        </Box>
      </Box>

      <Box className='grid grid-cols-12 gap-6'>
        <Box className='col-span-5'>
          <FormNumericField
            control={control}
            name={fieldPaths.insurance}
            label='Insurance'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
          />
        </Box>

        <Box className='col-span-7'>
          <Box className='space-y-5'>
            {tariffFields.map((field, tariffIndex) => (
              <AmountPercentageFieldPair
                key={field.id}
                control={control}
                productIndex={productIndex}
                fieldPrefix='tariffs'
                label='Tariffs'
                size='small'
                arrayIndex={tariffIndex}
                isDisabled={isBareCostEmpty}
              />
            ))}
          </Box>
          <Box className='flex justify-end pt-2'>
            <Button
              appearance='text'
              size='small'
              startIcon={<AddOutlined />}
              onClick={() => appendTariff(BLANK_TARIFF_ITEM)}
              disabled={isBareCostEmpty || hasTariffErrors}
            >
              Additional Tariffs
            </Button>
          </Box>
        </Box>
      </Box>

      <Box className='grid grid-cols-3 gap-6'>
        <AmountPercentageFieldPair
          control={control}
          productIndex={productIndex}
          fieldPrefix={CostBreakdownStandardField.Duties}
          label='Duties'
          size='small'
          isDisabled={isBareCostEmpty}
        />

        <AmountPercentageFieldPair
          control={control}
          productIndex={productIndex}
          fieldPrefix={CostBreakdownStandardField.Mpf}
          label='Merchandise Processing Fee'
          size='small'
          isDisabled={isBareCostEmpty}
        />

        <AmountPercentageFieldPair
          control={control}
          productIndex={productIndex}
          fieldPrefix={CostBreakdownStandardField.Hmf}
          label='Harbor Maintenance Fee'
          size='small'
          isDisabled={isBareCostEmpty}
        />
      </Box>

      <AtypicalSurchargesFormGroup
        control={control}
        baseFieldPath={fieldPaths.atypicalSurcharges}
        productIndex={productIndex}
      />

      <Box className='space-y-6'>
        <Box className='grid grid-cols-3 gap-6'>
          <FormNumericField
            control={control}
            name={fieldPaths.allocatedGrCostPercentage}
            label='Allocated GR Cost Percentage'
            {...COST_BREAKDOWN_PERCENTAGE_FIELD_PROPS}
            disabled
          />

          <FormNumericField
            control={control}
            name={fieldPaths.allocatedGrCostAmount}
            label='Allocated GR Cost Amount'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            disabled
          />

          <FormNumericField
            control={control}
            name={fieldPaths.allocatedGrCostPerUom}
            label='Allocated GR Cost per UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            disabled
          />
        </Box>

        <Box className='grid grid-cols-3 gap-6'>
          <FormNumericField
            control={control}
            name={fieldPaths.lastLaidDownCostPerUom}
            label='Last Laid-Down Cost per UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            disabled
          />

          <FormNumericField
            control={control}
            name={fieldPaths.totalLaidDownCostPerUom}
            label='Total Laid-Down Cost per UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            disabled
          />

          <FormNumericField
            control={control}
            name={fieldPaths.freightAndMiscCostPerUom}
            label='Freight and Misc Cost per UoM'
            {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
            disabled
          />
        </Box>

        <Box className='flex justify-end'>
          <FormCheckbox
            control={control}
            name={fieldPaths.isCompleted}
            label='Product Breakdown Complete'
            disabled={pricePerUom === null || pricePerUom === undefined}
          />
        </Box>
      </Box>
    </Box>
  )
}
